'use server'

import { prisma } from '@/lib/prisma'
import { revalidatePath } from 'next/cache'

// --- Categories ---
export async function getCategories() {
    return prisma.category.findMany({
        orderBy: { name: 'asc' }
    })
}

export async function addCategory(name: string) {
    try {
        await prisma.category.create({
            data: { name }
        })
        revalidatePath('/admin/songs')
        revalidatePath('/songlist')
        return { success: true }
    } catch (error) {
        console.error('Failed to add category:', error)
        return { success: false, error: 'Failed to add category' }
    }
}

export async function deleteCategory(id: string) {
    try {
        await prisma.category.delete({
            where: { id }
        })
        revalidatePath('/admin/songs')
        revalidatePath('/songlist')
        return { success: true }
    } catch (error) {
        console.error('Failed to delete category:', error)
        return { success: false, error: 'Failed to delete category' }
    }
}

// --- Songs ---
export async function getSongs(filter?: { categoryId?: string, language?: string }, userId?: string) {
    const where = {
        ...(filter?.categoryId ? { categoryId: filter.categoryId } : {}),
        ...(filter?.language ? { language: filter.language } : {})
    }

    const songs = await prisma.song.findMany({
        where,
        include: { category: true },
        orderBy: { averageRating: 'desc' }
    })

    // If userId is provided, fetch user's ratings for each song
    if (userId) {
        const userRatings = await prisma.rating.findMany({
            where: { userId, songId: { in: songs.map(s => s.id) } }
        })
        const ratingMap = new Map(userRatings.map(r => [r.songId, r.value]))

        return songs.map(song => ({
            ...song,
            userRating: ratingMap.get(song.id) || 0
        }))
    }

    return songs.map(song => ({ ...song, userRating: 0 }))
}

import { saveFile } from '@/lib/files'

export async function addSong(formData: FormData) {
    try {
        const title = formData.get('title') as string
        const artist = formData.get('artist') as string
        const language = formData.get('language') as string
        const categoryId = formData.get('categoryId') as string
        const youtubeUrl = formData.get('youtubeUrl') as string || null
        const audioFile = formData.get('audio') as File | null

        let audioUrl = null
        if (audioFile && audioFile.size > 0) {
            audioUrl = await saveFile(audioFile, 'uploads/songs')
        }

        await prisma.song.create({
            data: {
                title,
                artist,
                language,
                categoryId,
                audioUrl,
                youtubeUrl
            }
        })
        revalidatePath('/admin/songs')
        revalidatePath('/songlist')
        return { success: true }
    } catch (error) {
        console.error('Failed to add song:', error)
        return { success: false, error: 'Failed to add song' }
    }
}

export async function updateSong(formData: FormData) {
    try {
        const id = formData.get('id') as string
        const title = formData.get('title') as string
        const artist = formData.get('artist') as string
        const language = formData.get('language') as string
        const categoryId = formData.get('categoryId') as string
        const youtubeUrl = formData.get('youtubeUrl') as string || null
        const audioFile = formData.get('audio') as File | null

        const data: any = {
            title,
            artist,
            language,
            categoryId,
            youtubeUrl
        }

        if (audioFile && audioFile.size > 0) {
            data.audioUrl = await saveFile(audioFile, 'uploads/songs')
        }

        await prisma.song.update({
            where: { id },
            data
        })
        revalidatePath('/admin/songs')
        revalidatePath('/songlist')
        return { success: true }
    } catch (error) {
        console.error('Failed to update song:', error)
        return { success: false, error: 'Failed to update song' }
    }
}

export async function deleteSong(id: string) {
    try {
        await prisma.song.delete({ where: { id } })
        revalidatePath('/admin/songs')
        revalidatePath('/songlist')
        return { success: true }
    } catch (error) {
        console.error('Failed to delete song:', error)
        return { success: false, error: 'Failed to delete song' }
    }
}

// --- Ratings ---
export async function rateSong(songId: string, value: number, userId: string) {
    if (value < 1 || value > 5) return { success: false }

    try {
        // Upsert rating
        await prisma.rating.upsert({
            where: { userId_songId: { userId, songId } },
            update: { value },
            create: { userId, songId, value }
        })

        // Update aggregate on song
        const ratings = await prisma.rating.findMany({ where: { songId } })
        const total = ratings.length
        const sum = ratings.reduce((acc, r) => acc + r.value, 0)
        const avg = total > 0 ? sum / total : 0

        await prisma.song.update({
            where: { id: songId },
            data: {
                totalRatings: total,
                averageRating: avg
            }
        })

        revalidatePath('/songlist')
        return { success: true }
    } catch (e) {
        console.error(e)
        return { success: false }
    }
}

// --- Voting ---
export async function voteSong(songId: string, userId?: string) {
    try {
        // If we have a userId, check if they already voted (Logic skipped for now as per plan, but scaffolded)
        // if (userId) { ... }

        // Increment vote count on Song
        await prisma.song.update({
            where: { id: songId },
            data: { votes: { increment: 1 } }
        })

        // Create a generic tracking record (or use userId if available)
        if (userId) {
            // Check existing vote
            const existing = await prisma.songVote.findFirst({
                where: { userId, songId }
            })
            if (!existing) {
                await prisma.songVote.create({
                    data: { songId, userId }
                })
            }
        } else {
            // Anonymous vote tracking (optional)
            await prisma.songVote.create({
                data: { songId }
            })
        }

        revalidatePath('/songlist')
        return { success: true }
    } catch (error) {
        console.error('Failed to vote:', error)
        return { success: false, error: 'Failed to vote' }
    }
}
